// src/components/Bell.tsx
import React2 from "react";

// src/components/Mounter.tsx
import { useEffect, useRef } from "react";
import { jsx } from "react/jsx-runtime";
function Mounter({ mount }) {
  const ref = useRef(null);
  useEffect(() => {
    let unmount;
    const element = ref.current;
    if (element && mount) {
      const possibleUnmount = mount(element);
      if (possibleUnmount) {
        unmount = possibleUnmount;
      }
    }
    return () => {
      if (element && unmount) {
        unmount(element);
      }
    };
  }, [ref, mount]);
  return /* @__PURE__ */ jsx("div", { ref });
}

// src/components/Renderer.tsx
import { useCallback, useState } from "react";

// src/utils/createContextAndHook.ts
import React from "react";
function assertContextExists(contextVal, msgOrCtx) {
  if (!contextVal) {
    throw typeof msgOrCtx === "string" ? new Error(msgOrCtx) : new Error(`${msgOrCtx.displayName} not found`);
  }
}
var createContextAndHook = (displayName, options) => {
  const { assertCtxFn = assertContextExists } = options || {};
  const Ctx = React.createContext(void 0);
  Ctx.displayName = displayName;
  const useCtx = () => {
    const ctx = React.useContext(Ctx);
    assertCtxFn(ctx, `Component must be wrapped with ${Ctx.displayName}`);
    return ctx.value;
  };
  const useCtxWithoutGuarantee = () => {
    const ctx = React.useContext(Ctx);
    return ctx ? ctx.value : {};
  };
  return [Ctx, useCtx, useCtxWithoutGuarantee];
};

// src/context/RendererContext.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var [RendererContext, useRendererContext, useUnsafeRendererContext] = createContextAndHook("RendererContext");
var RendererProvider = (props) => {
  return /* @__PURE__ */ jsx2(RendererContext.Provider, { value: { value: props.value }, children: props.children });
};

// src/components/Renderer.tsx
import { createPortal } from "react-dom";
import { jsx as jsx3, jsxs } from "react/jsx-runtime";
var Renderer = (props) => {
  const { children } = props;
  const [mountedElements, setMountedElements] = useState(/* @__PURE__ */ new Map());
  const mountElement = useCallback(
    (el, mountedElement) => {
      setMountedElements((prev) => {
        const newMountedElements = new Map(prev);
        newMountedElements.set(el, mountedElement);
        return newMountedElements;
      });
      return () => {
        setMountedElements((prev) => {
          const newMountedElements = new Map(prev);
          newMountedElements.delete(el);
          return newMountedElements;
        });
      };
    },
    [setMountedElements]
  );
  return /* @__PURE__ */ jsxs(RendererProvider, { value: { mountElement }, children: [
    [...mountedElements].map(([element, mountedElement]) => {
      return createPortal(mountedElement, element);
    }),
    children
  ] });
};
var withRenderer = (WrappedComponent) => {
  const HOC = (props) => {
    return /* @__PURE__ */ jsx3(Renderer, { children: /* @__PURE__ */ jsx3(WrappedComponent, { ...props }) });
  };
  HOC.displayName = `WithRenderer(${WrappedComponent.displayName || WrappedComponent.name || "Component"})`;
  return HOC;
};

// src/context/NovuUIContext.tsx
import { jsx as jsx4 } from "react/jsx-runtime";
var [NovuUIContext, useNovuUIContext, useUnsafeNovuUIContext] = createContextAndHook("NovuUIContext");
var NovuUIProvider = (props) => {
  return /* @__PURE__ */ jsx4(NovuUIContext.Provider, { value: { value: props.value }, children: props.children });
};

// src/components/Bell.tsx
import { jsx as jsx5 } from "react/jsx-runtime";
var _Bell = React2.memo((props) => {
  const { renderBell } = props;
  const { novuUI } = useNovuUIContext();
  const { mountElement } = useRendererContext();
  const mount = React2.useCallback(
    (element) => {
      return novuUI.mountComponent({
        name: "Bell",
        element,
        props: renderBell ? { renderBell: (el, unreadCount) => mountElement(el, renderBell(unreadCount)) } : void 0
      });
    },
    [renderBell]
  );
  return /* @__PURE__ */ jsx5(Mounter, { mount });
});
var Bell = withRenderer(_Bell);

// src/components/Inbox.tsx
import React4, { useMemo as useMemo2 } from "react";

// src/hooks/NovuProvider.tsx
import { Novu } from "@novu/js";
import { createContext, useContext, useMemo } from "react";
import { jsx as jsx6 } from "react/jsx-runtime";
var version = "2.6.5";
var name = "@novu/react";
var baseUserAgent = `${name}@${version}`;
var NovuContext = createContext(void 0);
var NovuProvider = ({
  children,
  applicationIdentifier,
  subscriberId,
  subscriberHash,
  backendUrl,
  socketUrl,
  useCache
}) => {
  return /* @__PURE__ */ jsx6(
    InternalNovuProvider,
    {
      applicationIdentifier,
      subscriberId,
      subscriberHash,
      backendUrl,
      socketUrl,
      useCache,
      userAgentType: "hooks",
      children
    }
  );
};
var InternalNovuProvider = ({
  children,
  applicationIdentifier,
  subscriberId,
  subscriberHash,
  backendUrl,
  socketUrl,
  useCache,
  userAgentType
}) => {
  const novu = useMemo(
    () => new Novu({
      applicationIdentifier,
      subscriberId,
      subscriberHash,
      backendUrl,
      socketUrl,
      useCache,
      __userAgent: `${baseUserAgent} ${userAgentType}`
    }),
    [applicationIdentifier, subscriberId, subscriberHash, backendUrl, socketUrl, useCache, userAgentType]
  );
  return /* @__PURE__ */ jsx6(NovuContext.Provider, { value: novu, children });
};
var useNovu = () => {
  const context = useContext(NovuContext);
  if (!context) {
    throw new Error("useNovu must be used within a <NovuProvider />");
  }
  return context;
};
var useUnsafeNovu = () => {
  const context = useContext(NovuContext);
  return context;
};

// src/components/NovuUI.tsx
import { NovuUI as NovuUIClass } from "@novu/js/ui";
import { useEffect as useEffect2, useState as useState2 } from "react";

// src/hooks/internal/useDataRef.ts
import { useRef as useRef2 } from "react";
var useDataRef = (data) => {
  const ref = useRef2(data);
  ref.current = data;
  return ref;
};

// src/components/NovuUI.tsx
import { jsx as jsx7 } from "react/jsx-runtime";
var NovuUI = ({ options, novu, children }) => {
  const optionsRef = useDataRef({ ...options, novu });
  const [novuUI, setNovuUI] = useState2();
  useEffect2(() => {
    const novu2 = new NovuUIClass(optionsRef.current);
    setNovuUI(novu2);
    return () => {
      novu2.unmount();
    };
  }, []);
  useEffect2(() => {
    if (!novuUI) {
      return;
    }
    novuUI.updateAppearance(options.appearance);
    novuUI.updateLocalization(options.localization);
    novuUI.updateTabs(options.tabs);
    novuUI.updateOptions(options.options);
    novuUI.updateRouterPush(options.routerPush);
  }, [options]);
  if (!novuUI) {
    return null;
  }
  return /* @__PURE__ */ jsx7(NovuUIProvider, { value: { novuUI }, children });
};

// src/components/Inbox.tsx
import { jsx as jsx8 } from "react/jsx-runtime";
var _DefaultInbox = (props) => {
  const {
    open,
    renderNotification,
    renderBell,
    onNotificationClick,
    onPrimaryActionClick,
    onSecondaryActionClick,
    placement,
    placementOffset
  } = props;
  const { novuUI } = useNovuUIContext();
  const { mountElement } = useRendererContext();
  const mount = React4.useCallback(
    (element) => {
      return novuUI.mountComponent({
        name: "Inbox",
        props: {
          open,
          renderNotification: renderNotification ? (el, notification) => mountElement(el, renderNotification(notification)) : void 0,
          renderBell: renderBell ? (el, unreadCount) => mountElement(el, renderBell(unreadCount)) : void 0,
          onNotificationClick,
          onPrimaryActionClick,
          onSecondaryActionClick,
          placementOffset,
          placement
        },
        element
      });
    },
    [open, renderNotification, renderBell, onNotificationClick, onPrimaryActionClick, onSecondaryActionClick]
  );
  return /* @__PURE__ */ jsx8(Mounter, { mount });
};
var DefaultInbox = withRenderer(_DefaultInbox);
var Inbox = React4.memo((props) => {
  const { applicationIdentifier, subscriberId, subscriberHash, backendUrl, socketUrl } = props;
  const novu = useUnsafeNovu();
  if (novu) {
    return /* @__PURE__ */ jsx8(InboxChild, { ...props });
  }
  return /* @__PURE__ */ jsx8(
    InternalNovuProvider,
    {
      applicationIdentifier,
      subscriberId,
      subscriberHash,
      backendUrl,
      socketUrl,
      userAgentType: "components",
      children: /* @__PURE__ */ jsx8(InboxChild, { ...props })
    }
  );
});
var InboxChild = React4.memo((props) => {
  const {
    localization,
    appearance,
    tabs,
    preferencesFilter,
    routerPush,
    applicationIdentifier,
    subscriberId,
    subscriberHash,
    backendUrl,
    socketUrl
  } = props;
  const novu = useNovu();
  const options = useMemo2(() => {
    return {
      localization,
      appearance,
      tabs,
      preferencesFilter,
      routerPush,
      options: { applicationIdentifier, subscriberId, subscriberHash, backendUrl, socketUrl }
    };
  }, [
    localization,
    appearance,
    tabs,
    preferencesFilter,
    applicationIdentifier,
    subscriberId,
    subscriberHash,
    backendUrl,
    socketUrl
  ]);
  if (isWithChildrenProps(props)) {
    return /* @__PURE__ */ jsx8(NovuUI, { options, novu, children: props.children });
  }
  const {
    open,
    renderNotification,
    renderBell,
    onNotificationClick,
    onPrimaryActionClick,
    onSecondaryActionClick,
    placementOffset,
    placement
  } = props;
  return /* @__PURE__ */ jsx8(NovuUI, { options, novu, children: /* @__PURE__ */ jsx8(
    DefaultInbox,
    {
      open,
      renderNotification,
      renderBell,
      onNotificationClick,
      onPrimaryActionClick,
      onSecondaryActionClick,
      placement,
      placementOffset
    }
  ) });
});
function isWithChildrenProps(props) {
  return "children" in props;
}

// src/components/Preferences.tsx
import React5 from "react";
import { jsx as jsx9 } from "react/jsx-runtime";
var Preferences = () => {
  const { novuUI } = useNovuUIContext();
  const mount = React5.useCallback((element) => {
    return novuUI.mountComponent({
      name: "Preferences",
      element
    });
  }, []);
  return /* @__PURE__ */ jsx9(Mounter, { mount });
};

// src/components/Notifications.tsx
import React6 from "react";
import { jsx as jsx10 } from "react/jsx-runtime";
var _Notifications = React6.memo((props) => {
  const { onNotificationClick, onPrimaryActionClick, renderNotification, onSecondaryActionClick } = props;
  const { novuUI } = useNovuUIContext();
  const { mountElement } = useRendererContext();
  const mount = React6.useCallback(
    (element) => {
      return novuUI.mountComponent({
        name: "Notifications",
        element,
        props: {
          renderNotification: renderNotification ? (el, notification) => mountElement(el, renderNotification(notification)) : void 0,
          onNotificationClick,
          onPrimaryActionClick,
          onSecondaryActionClick
        }
      });
    },
    [renderNotification, onNotificationClick, onPrimaryActionClick, onSecondaryActionClick]
  );
  return /* @__PURE__ */ jsx10(Mounter, { mount });
});
var Notifications = withRenderer(_Notifications);

// src/components/InboxContent.tsx
import React7 from "react";
import { jsx as jsx11 } from "react/jsx-runtime";
var _InboxContent = React7.memo((props) => {
  const {
    onNotificationClick,
    onPrimaryActionClick,
    renderNotification,
    onSecondaryActionClick,
    initialPage,
    hideNav
  } = props;
  const { novuUI } = useNovuUIContext();
  const { mountElement } = useRendererContext();
  const mount = React7.useCallback(
    (element) => {
      return novuUI.mountComponent({
        name: "InboxContent",
        element,
        props: {
          renderNotification: renderNotification ? (el, notification) => mountElement(el, renderNotification(notification)) : void 0,
          onNotificationClick,
          onPrimaryActionClick,
          onSecondaryActionClick,
          initialPage,
          hideNav
        }
      });
    },
    [renderNotification, onNotificationClick, onPrimaryActionClick, onSecondaryActionClick]
  );
  return /* @__PURE__ */ jsx11(Mounter, { mount });
});
var InboxContent = withRenderer(_InboxContent);

// src/hooks/useNotifications.ts
import { useState as useState3, useEffect as useEffect3, useRef as useRef3 } from "react";
import { isSameFilter } from "@novu/js";
var useNotifications = (props) => {
  const { tags, read, archived = false, limit, onSuccess, onError } = props || {};
  const filterRef = useRef3(void 0);
  const { notifications, on } = useNovu();
  const [data, setData] = useState3();
  const [error, setError] = useState3();
  const [isLoading, setIsLoading] = useState3(true);
  const [isFetching, setIsFetching] = useState3(false);
  const [hasMore, setHasMore] = useState3(false);
  const length = data?.length;
  const after = length ? data[length - 1].id : void 0;
  const sync = (event) => {
    if (!event.data || filterRef.current && !isSameFilter(filterRef.current, event.data.filter)) {
      return;
    }
    setData(event.data.notifications);
    setHasMore(event.data.hasMore);
  };
  useEffect3(() => {
    const cleanup = on("notifications.list.updated", sync);
    return () => {
      cleanup();
    };
  }, []);
  useEffect3(() => {
    const newFilter = { tags, read, archived };
    if (filterRef.current && isSameFilter(filterRef.current, newFilter)) {
      return;
    }
    notifications.clearCache({ filter: filterRef.current });
    filterRef.current = newFilter;
    fetchNotifications({ refetch: true });
  }, [tags, read, archived]);
  const fetchNotifications = async (options) => {
    if (options?.refetch) {
      setError(void 0);
      setIsLoading(true);
      setIsFetching(false);
    }
    setIsFetching(true);
    const response = await notifications.list({
      tags,
      read,
      archived,
      limit,
      after: options?.refetch ? void 0 : after
    });
    if (response.error) {
      setError(response.error);
      onError?.(response.error);
    } else {
      onSuccess?.(response.data.notifications);
      setData(response.data.notifications);
      setHasMore(response.data.hasMore);
    }
    setIsLoading(false);
    setIsFetching(false);
  };
  const refetch = () => {
    notifications.clearCache({ filter: { tags, read, archived } });
    return fetchNotifications({ refetch: true });
  };
  const fetchMore = async () => {
    if (!hasMore || isFetching) return;
    return fetchNotifications();
  };
  const readAll = async () => {
    return await notifications.readAll({ tags });
  };
  const archiveAll = async () => {
    return await notifications.archiveAll({ tags });
  };
  const archiveAllRead = async () => {
    return await notifications.archiveAllRead({ tags });
  };
  return {
    readAll,
    archiveAll,
    archiveAllRead,
    notifications: data,
    error,
    isLoading,
    isFetching,
    refetch,
    fetchMore,
    hasMore
  };
};

// src/hooks/usePreferences.ts
import { useEffect as useEffect4, useState as useState4 } from "react";
var usePreferences = (props) => {
  const { onSuccess, onError } = props || {};
  const [data, setData] = useState4();
  const { preferences, on } = useNovu();
  const [error, setError] = useState4();
  const [isLoading, setIsLoading] = useState4(true);
  const [isFetching, setIsFetching] = useState4(false);
  const sync = (event) => {
    if (!event.data) {
      return;
    }
    setData(event.data);
  };
  useEffect4(() => {
    fetchPreferences();
    const listUpdatedCleanup = on("preferences.list.updated", sync);
    const listPendingCleanup = on("preferences.list.pending", sync);
    const listResolvedCleanup = on("preferences.list.resolved", sync);
    return () => {
      listUpdatedCleanup();
      listPendingCleanup();
      listResolvedCleanup();
    };
  }, []);
  const fetchPreferences = async () => {
    setIsFetching(true);
    const response = await preferences.list(props?.filter);
    if (response.error) {
      setError(response.error);
      onError?.(response.error);
    } else {
      onSuccess?.(response.data);
    }
    setIsLoading(false);
    setIsFetching(false);
  };
  const refetch = () => {
    preferences.cache.clearAll();
    return fetchPreferences();
  };
  return {
    preferences: data,
    error,
    isLoading,
    isFetching,
    refetch
  };
};

// src/hooks/useCounts.ts
import { useEffect as useEffect7, useState as useState6 } from "react";
import { areTagsEqual } from "@novu/js";

// src/hooks/internal/useWebsocketEvent.ts
import { useEffect as useEffect6 } from "react";

// src/utils/requestLock.ts
function requestLock(id, cb) {
  if (!("locks" in navigator)) {
    cb(id);
    return () => {
    };
  }
  let isFulfilled = false;
  let promiseResolve;
  const promise = new Promise((resolve) => {
    promiseResolve = resolve;
  });
  navigator.locks.request(id, () => {
    if (!isFulfilled) {
      cb(id);
    }
    return promise;
  });
  return () => {
    isFulfilled = true;
    promiseResolve();
  };
}

// src/hooks/internal/useBrowserTabsChannel.ts
import { useEffect as useEffect5, useState as useState5 } from "react";
var useBrowserTabsChannel = ({
  channelName,
  onMessage
}) => {
  const [tabsChannel] = useState5(
    typeof BroadcastChannel !== "undefined" ? new BroadcastChannel(channelName) : void 0
  );
  const postMessage = (data) => {
    tabsChannel?.postMessage(data);
  };
  useEffect5(() => {
    const listener = (event) => {
      onMessage(event.data);
    };
    tabsChannel?.addEventListener("message", listener);
    return () => {
      tabsChannel?.removeEventListener("message", listener);
    };
  }, []);
  return { postMessage };
};

// src/hooks/internal/useWebsocketEvent.ts
var useWebSocketEvent = ({
  event: webSocketEvent,
  eventHandler: onMessage
}) => {
  const novu = useNovu();
  const { postMessage } = useBrowserTabsChannel({ channelName: `nv.${webSocketEvent}`, onMessage });
  const updateReadCount = (data) => {
    onMessage(data);
    postMessage(data);
  };
  useEffect6(() => {
    let cleanup;
    const resolveLock = requestLock(`nv.${webSocketEvent}`, () => {
      cleanup = novu.on(webSocketEvent, updateReadCount);
    });
    return () => {
      if (cleanup) {
        cleanup();
      }
      resolveLock();
    };
  }, []);
};

// src/hooks/useCounts.ts
var useCounts = (props) => {
  const { filters, onSuccess, onError } = props;
  const { notifications } = useNovu();
  const [error, setError] = useState6();
  const [counts, setCounts] = useState6();
  const [isLoading, setIsLoading] = useState6(true);
  const [isFetching, setIsFetching] = useState6(false);
  const sync = async (notification) => {
    const existingCounts = counts ?? new Array(filters.length).fill(void 0);
    let countFiltersToFetch = [];
    if (notification) {
      for (let i = 0; i < existingCounts.length; i++) {
        const filter = filters[i];
        if (areTagsEqual(filter.tags, notification.tags)) {
          countFiltersToFetch.push(filter);
        }
      }
    } else {
      countFiltersToFetch = filters;
    }
    if (countFiltersToFetch.length === 0) {
      return;
    }
    setIsFetching(true);
    const countsRes = await notifications.count({ filters: countFiltersToFetch });
    setIsFetching(false);
    setIsLoading(false);
    if (countsRes.error) {
      setError(countsRes.error);
      onError?.(countsRes.error);
      return;
    }
    const data = countsRes.data;
    onSuccess?.(data.counts);
    setCounts((oldCounts) => {
      const newCounts = [];
      const countsReceived = data.counts;
      for (let i = 0; i < existingCounts.length; i++) {
        const countReceived = countsReceived.find((c) => areTagsEqual(c.filter.tags, existingCounts[i]?.filter.tags));
        newCounts.push(countReceived || oldCounts[i]);
      }
      return newCounts;
    });
  };
  useWebSocketEvent({
    event: "notifications.notification_received",
    eventHandler: (data) => {
      sync(data.result);
    }
  });
  useWebSocketEvent({
    event: "notifications.unread_count_changed",
    eventHandler: () => {
      sync();
    }
  });
  useEffect7(() => {
    setError(void 0);
    setIsLoading(true);
    setIsFetching(false);
    sync();
  }, [JSON.stringify(filters)]);
  const refetch = async () => {
    await sync();
  };
  return { counts, error, refetch, isLoading, isFetching };
};
export {
  Bell,
  Inbox,
  InboxContent,
  InternalNovuProvider,
  Notifications,
  NovuProvider,
  Preferences,
  useCounts,
  useNotifications,
  useNovu,
  usePreferences,
  useUnsafeNovu
};
//# sourceMappingURL=index.mjs.map